export type PermissionType = 'menu' | 'page' | 'button';

/**
 * 权限点实际调用的 API 路由，用于后端数据权限。
 * 每项格式：METHOD /path，path 与 OpenAPI 一致，路径参数用占位符如 {user_id}、{role_id}。
 */
export type ApiRouteItem = string;

export interface PermissionManifestItem {
  perm_key: string;
  name: string;
  perm_type: PermissionType;
  module?: string;
  parent_key?: string;
  route_path?: string;
  status?: 'active' | 'inactive';
  source?: 'frontend' | 'manual';
  meta_json?: Record<string, unknown>;
  /** 该权限点实际调用的 API 路由，同步时传给后端，便于一键启用数据权限 */
  api_routes?: ApiRouteItem[] | null;
}

/**
 * RBAC 权限点清单（单一真相源）。
 * 第一阶段先覆盖员工管理与角色权限建设能力，后续功能迭代时持续补充。
 *
 * api_routes：该权限点实际调用的接口路径（METHOD /path），与 OpenAPI path 一致，
 * 路径参数用占位符 {user_id}、{role_id}。同步 manifest 时一并传给后端，便于后端一键启用数据权限。
 */
export const permissionManifest: PermissionManifestItem[] = [
{
  "perm_key": "tools.marketing_push.view",
  "name": "查看营销推送",
  "perm_type": "page",
  "module": "tools",
  "parent_key": "tools",
  "route_path": "/tools/marketing-push",
  "status": "active",
  "source": "frontend",
  "api_routes": [
    "GET /api/admin/marketing-push",
    "GET /api/admin/marketing-push/{plan_id}/records",
    "POST /api/admin/marketing-push/preview"
  ]
},
{
  "perm_key": "tools.marketing_push.edit",
  "name": "编辑营销模板和时间",
  "perm_type": "button",
  "module": "tools",
  "parent_key": "tools.marketing_push.view",
  "route_path": "/tools/marketing-push",
  "status": "active",
  "source": "frontend",
  "api_routes": [
    "GET /api/admin/marketing-push",
    "GET /api/admin/marketing-push/{plan_id}/records",
    "POST /api/admin/marketing-push/preview",
    "PUT /api/admin/marketing-push/{plan_id}",
    "POST /api/admin/marketing-push"
  ]
},
{
  "perm_key": "tools.marketing_push.send",
  "name": "试发营销推送",
  "perm_type": "button",
  "module": "tools",
  "parent_key": "tools.marketing_push.view",
  "route_path": "/tools/marketing-push",
  "status": "active",
  "source": "frontend",
  "api_routes": [
    "GET /api/admin/marketing-push",
    "GET /api/admin/marketing-push/{plan_id}/records",
    "POST /api/admin/marketing-push/preview",
    "POST /api/admin/marketing-push/test-send"
  ]
},
{
  "perm_key": "tools.bot_activation_push.view",
  "name": "查看 Bot 促活推送",
  "perm_type": "page",
  "module": "tools",
  "parent_key": "tools",
  "route_path": "/tools/bot-activation-push",
  "status": "active",
  "source": "frontend",
  "api_routes": [
    "GET /api/admin/bot-activation-push",
    "GET /api/admin/bot-activation-push/{plan_id}/records"
  ]
},
{
  "perm_key": "tools.bot_activation_push.edit",
  "name": "编辑 Bot 促活推送",
  "perm_type": "button",
  "module": "tools",
  "parent_key": "tools.bot_activation_push.view",
  "route_path": "/tools/bot-activation-push",
  "status": "active",
  "source": "frontend",
  "api_routes": [
    "GET /api/admin/bot-activation-push",
    "GET /api/admin/bot-activation-push/{plan_id}/records",
    "PUT /api/admin/bot-activation-push"
  ]
},
  {
    perm_key: 'staff',
    name: '员工管理',
    perm_type: 'menu',
    module: 'staff',
    route_path: '/staff',
    status: 'active',
    source: 'frontend',
    meta_json: {
      icon: 'team',
      order: 800,
    },
    api_routes: ['GET /api/admin/users/me/authz'],
  },
  {
    perm_key: 'staff.list.view',
    name: '员工列表页面',
    perm_type: 'page',
    module: 'staff',
    parent_key: 'staff',
    route_path: '/staff/list',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/users',
      'GET /api/admin/rbac/roles',
    ],
  },
  {
    perm_key: 'staff.detail.view',
    name: '员工详情页面',
    perm_type: 'page',
    module: 'staff',
    parent_key: 'staff',
    route_path: '/staff/users/:id',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/users/{user_id}'],
  },
  {
    perm_key: 'staff.role_permission.view',
    name: '角色和权限页面',
    perm_type: 'page',
    module: 'staff',
    parent_key: 'staff',
    route_path: '/staff/roles-permissions',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/users/me/authz',
      'GET /api/admin/rbac/roles',
      'GET /api/admin/rbac/permissions',
      'GET /api/admin/rbac/roles/{role_id}/permissions',
    ],
  },
  {
    perm_key: 'staff.user.create',
    name: '新建员工',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/users'],
  },
  {
    perm_key: 'staff.user.update',
    name: '编辑员工',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/users/{user_id}'],
  },
  {
    perm_key: 'staff.user.delete',
    name: '删除员工',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/users/{user_id}'],
  },
  {
    perm_key: 'staff.user.reset_password',
    name: '重置员工密码',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/users/{user_id}/password'],
  },
  {
    perm_key: 'staff.user.assign_roles',
    name: '分配角色',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/users/{user_id}'],
  },
  {
    perm_key: 'staff.role.create',
    name: '新建角色',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.role_permission.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/rbac/roles'],
  },
  {
    perm_key: 'staff.role.update',
    name: '编辑角色',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.role_permission.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/rbac/roles/{role_id}'],
  },
  {
    perm_key: 'staff.role.delete',
    name: '删除角色',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.role_permission.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/rbac/roles/{role_id}'],
  },
  {
    perm_key: 'staff.role.assign_permissions',
    name: '分配角色权限',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.role_permission.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/rbac/roles/{role_id}/permissions'],
  },
  {
    perm_key: 'staff.permission_manifest.sync',
    name: '同步权限清单',
    perm_type: 'button',
    module: 'staff',
    parent_key: 'staff.role_permission.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/rbac/permissions/sync'],
  },
  {
    perm_key: 'staff.operation_logs.view',
    name: '操作记录页面（查看自己的记录）',
    perm_type: 'page',
    module: 'staff',
    parent_key: 'staff',
    route_path: '/staff/operation-logs',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/operation-logs/my'],
  },
  {
    perm_key: 'staff.operation_logs.view_all',
    name: '操作记录页面（查看全部记录，仅 system_admin）',
    perm_type: 'page',
    module: 'staff',
    parent_key: 'staff',
    route_path: '/staff/operation-logs',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/operation-logs'],
  },
  // ---------- 个人设置 ----------
  {
    perm_key: 'account',
    name: '个人设置',
    perm_type: 'menu',
    module: 'account',
    route_path: '/account',
    status: 'active',
    source: 'frontend',
    meta_json: { order: 50 },
    api_routes: ['GET /api/admin/users/me', 'PUT /api/admin/users/me'],
  },
  {
    perm_key: 'account.settings.view',
    name: '个人设置页面',
    perm_type: 'page',
    module: 'account',
    parent_key: 'account',
    route_path: '/account/settings',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/users/me', 'PUT /api/admin/users/me'],
  },
  {
    perm_key: 'account.api_key.view',
    name: '个人设置 - API Key 管理',
    perm_type: 'page',
    module: 'account',
    parent_key: 'account',
    route_path: '/account/settings',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/api-keys',
      'POST /api/admin/api-keys',
      'PATCH /api/admin/api-keys/{api_key_id}',
      'DELETE /api/admin/api-keys/{api_key_id}',
      'GET /api/admin/api-keys/available-apis',
    ],
  },
  // ---------- 求职用户 ----------
  {
    perm_key: 'job_seekers',
    name: '求职用户',
    perm_type: 'menu',
    module: 'job_seekers',
    route_path: '/job-seekers',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'team', order: 100 },
    api_routes: ['GET /api/admin/c_users'],
  },
  {
    perm_key: 'job_seekers.users.view',
    name: '求职用户列表',
    perm_type: 'page',
    module: 'job_seekers',
    parent_key: 'job_seekers',
    route_path: '/job-seekers/users',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/c_users'],
  },
  {
    perm_key: 'job_seekers.users.detail.view',
    name: '求职用户详情',
    perm_type: 'page',
    module: 'job_seekers',
    parent_key: 'job_seekers',
    route_path: '/job-seekers/users/:id',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/c_users/{user_id}'],
  },
  {
    perm_key: 'job_seekers.users.create',
    name: '创建求职用户',
    perm_type: 'button',
    module: 'job_seekers',
    parent_key: 'job_seekers.users.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/c_users'],
  },
  {
    perm_key: 'job_seekers.users.membership.update',
    name: '编辑 B 用户会员权益',
    perm_type: 'button',
    module: 'job_seekers',
    parent_key: 'job_seekers.users.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/c_users/{user_id}/membership',
      'PUT /api/admin/c_users/{user_id}/membership',
      'GET /api/admin/commercial/accounts/{user_id}',
      'POST /api/admin/commercial/accounts/{user_id}/job-post-item-grants',
      'POST /api/admin/commercial/accounts/{user_id}/resume-contact-item-grants',
      'POST /api/admin/commercial/accounts/{user_id}/job-boost-item-grants',
    ],
  },
  {
    perm_key: 'job_seekers.users.reset_password',
    name: '重置求职用户密码',
    perm_type: 'button',
    module: 'job_seekers',
    parent_key: 'job_seekers.users.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/c_users/{user_id}/password'],
  },
  {
    perm_key: 'job_seekers.users.restrict_user',
    name: '禁言或封禁求职用户',
    perm_type: 'button',
    module: 'job_seekers',
    parent_key: 'job_seekers.users.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/c_users/{user_id}/restriction'],
  },
  {
    perm_key: 'job_seekers.job_intentions.view',
    name: '求职意向列表',
    perm_type: 'page',
    module: 'job_seekers',
    parent_key: 'job_seekers',
    route_path: '/job-seekers/job-intentions',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/job_intentions'],
  },
  {
    perm_key: 'job_seekers.job_intentions.detail.view',
    name: '求职意向详情',
    perm_type: 'page',
    module: 'job_seekers',
    parent_key: 'job_seekers',
    route_path: '/job-seekers/job-intentions/:id',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/job_intentions/{job_intention_id}'],
  },
  {
    perm_key: 'job_seekers.resumes.view',
    name: '简历列表',
    perm_type: 'page',
    module: 'job_seekers',
    parent_key: 'job_seekers',
    route_path: '/job-seekers/resumes',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/resumes'],
  },
  {
    perm_key: 'job_seekers.resumes.detail.view',
    name: '简历详情',
    perm_type: 'page',
    module: 'job_seekers',
    parent_key: 'job_seekers',
    route_path: '/job-seekers/resumes/:id',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/resumes/{resume_id}', 'GET /api/admin/resumes/combined-detail'],
  },
  {
    perm_key: 'job_seekers.resume_source_files.view',
    name: '简历附件审核列表',
    perm_type: 'page',
    module: 'job_seekers',
    parent_key: 'job_seekers',
    route_path: '/job-seekers/resume-source-files',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/resumes/source-files',
      'GET /api/admin/resumes/source-files/{file_id}/preview-url',
    ],
  },
  {
    perm_key: 'job_seekers.resume_source_files.review',
    name: '人工审核简历附件',
    perm_type: 'button',
    module: 'job_seekers',
    parent_key: 'job_seekers.resume_source_files.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/resumes/source-files/{file_id}/review'],
  },
  // ---------- 企业管理 ----------
  {
    perm_key: 'companies',
    name: '企业管理',
    perm_type: 'menu',
    module: 'companies',
    route_path: '/companies',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'bank', order: 200 },
    api_routes: ['GET /api/admin/companies'],
  },
  {
    perm_key: 'companies.list.view',
    name: '企业列表',
    perm_type: 'page',
    module: 'companies',
    parent_key: 'companies',
    route_path: '/companies/list',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/companies', 'GET /api/admin/companies/{company_id}'],
  },
  {
    perm_key: 'companies.config.view',
    name: '企业配置',
    perm_type: 'page',
    module: 'companies',
    parent_key: 'companies',
    route_path: '/companies/config',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/dictionaries', 'GET /api/admin/dictionaries/tree'],
  },
  // ---------- 岗位管理 ----------
  {
    perm_key: 'jobs',
    name: '岗位管理',
    perm_type: 'menu',
    module: 'jobs',
    route_path: '/jobs',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'solution', order: 210 },
    api_routes: ['GET /api/admin/jds'],
  },
  {
    perm_key: 'jobs.list.view',
    name: '岗位列表',
    perm_type: 'page',
    module: 'jobs',
    parent_key: 'jobs',
    route_path: '/jobs/list',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/jds', 'GET /api/admin/jds/{jd_id}'],
  },
  {
    perm_key: 'jobs.generation.view',
    name: '岗位生成',
    perm_type: 'page',
    module: 'jobs',
    parent_key: 'jobs',
    route_path: '/jobs/generation',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/jds/generation-tasks',
      'POST /api/admin/jds/generation-tasks',
      'GET /api/admin/jds/generation-tasks/{task_id}',
      'GET /api/admin/jds/generation-tasks/batch/{batch_id}',
      'POST /api/admin/jds/generation-tasks/batch',
    ],
  },
  {
    perm_key: 'jobs.generation.dashboard.view',
    name: '岗位数据看板',
    perm_type: 'page',
    module: 'jobs',
    parent_key: 'jobs.generation.view',
    route_path: '/jobs/generation-dashboard',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/jds/generation-tasks/dashboard',
      'GET /api/admin/jds/generation-tasks/dashboard/full-by-source-entity',
    ],
  },
  {
    perm_key: 'jobs.job.create',
    name: '新建岗位',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/jds'],
  },
  {
    perm_key: 'jobs.job.export',
    name: '导出岗位',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/jds'],
  },
  {
    perm_key: 'jobs.job.update',
    name: '编辑岗位',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/jds/{jd_id}'],
  },
  {
    perm_key: 'jobs.job.delete',
    name: '删除岗位',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/jds/{jd_id}'],
  },
  {
    perm_key: 'jobs.job.publish',
    name: '发布岗位',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/jds/{jd_id}/publish'],
  },
  {
    perm_key: 'jobs.job.unpublish',
    name: '下架岗位',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/jds/{jd_id}/unpublish'],
  },
  {
    perm_key: 'jobs.job.translate',
    name: '岗位翻译',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'POST /api/admin/jds/{jd_id}/translate-submit',
      'GET /api/admin/jds/{jd_id}/translate-preview',
      'GET /api/admin/jds/{jd_id}/translation-status',
    ],
  },
  {
    perm_key: 'jobs.job.pin',
    name: '岗位置顶',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'POST /api/admin/jds/{jd_id}/pin',
      'POST /api/admin/jds/{jd_id}/unpin',
    ],
  },
  {
    perm_key: 'jobs.job.review',
    name: '岗位审核',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/jds/{jd_id}/review'],
  },
  {
    perm_key: 'jobs.review_assignment.manage',
    name: '审核分配',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.list.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/jds',
      'GET /api/admin/jds/review-assignment-groups',
      'POST /api/admin/jds/assign-review',
      'POST /api/admin/jds/unassign-review',
    ],
  },
  {
    perm_key: 'jobs.review_records.view',
    name: '岗位审核记录（全局）',
    perm_type: 'page',
    module: 'jobs',
    parent_key: 'jobs',
    route_path: '/jobs/review-records',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/jds/all-review-records',
      'GET /api/admin/jds/locate-page',
      'GET /api/admin/dictionaries',
      'GET /api/admin/dictionaries/tree',
      'POST /api/admin/dictionaries',
      'GET /api/admin/dictionaries/{id}',
      'PUT /api/admin/dictionaries/{id}',
      'DELETE /api/admin/dictionaries/{id}',
    ],
  },
  {
    perm_key: 'jobs.blacklist.view',
    name: '岗位黑名单',
    perm_type: 'page',
    module: 'jobs',
    parent_key: 'jobs',
    route_path: '/jobs/blacklist',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/jds/blacklist/items',
      'GET /api/admin/jds/blacklist/items/{item_id}',
      'POST /api/admin/jds/blacklist/items',
      'PATCH /api/admin/jds/blacklist/items/{item_id}',
      'DELETE /api/admin/jds/blacklist/items/{item_id}',
      'GET /api/admin/jds/blacklist/items/{item_id}/preview',
      'GET /api/admin/jds/blacklist/hits',
      'GET /api/admin/jds/{jd_id}',
    ],
  },
  {
    perm_key: 'jobs.generation.create',
    name: '创建岗位生成任务',
    perm_type: 'button',
    module: 'jobs',
    parent_key: 'jobs.generation.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'POST /api/admin/jds/generation-tasks',
      'POST /api/admin/jds/generation-tasks/batch',
    ],
  },
  // ---------- 配置管理 ----------
  {
    perm_key: 'config',
    name: '配置管理',
    perm_type: 'menu',
    module: 'config',
    route_path: '/config',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'setting', order: 300 },
    api_routes: ['GET /api/admin/dictionaries', 'GET /api/admin/locations/tree'],
  },
  {
    perm_key: 'config.job_positions.view',
    name: '岗位标签',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/job-positions',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/dictionaries', 'GET /api/admin/dictionaries/tree'],
  },
  {
    perm_key: 'config.job_groups.view',
    name: '岗位分组配置',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/job-groups',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/job-position-groups',
      'POST /api/admin/job-position-groups',
      'PUT /api/admin/job-position-groups/{group_id}',
      'DELETE /api/admin/job-position-groups/{group_id}',
      'GET /api/admin/dictionaries/job_position/tree-with-count',
    ],
  },
  {
    perm_key: 'config.hot_job_groups.view',
    name: '热门分组配置',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/hot-job-groups',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/hot-job-groups',
      'POST /api/admin/hot-job-groups',
      'PUT /api/admin/hot-job-groups/{hot_group_id}',
      'DELETE /api/admin/hot-job-groups/{hot_group_id}',
      'GET /api/admin/dictionaries/tree',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'config.hot_search_words.view',
    name: '热搜词配置',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/hot-search-words',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/dictionaries',
      'POST /api/admin/dictionaries',
      'PUT /api/admin/dictionaries/{dictionary_id}',
      'DELETE /api/admin/dictionaries/{dictionary_id}',
    ],
  },
  {
    perm_key: 'config.visitor_contact_limit.view',
    name: '游客联系限制',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/visitor-contact-limit',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/config/visitor-contact-limit',
      'PUT /api/admin/config/visitor-contact-limit',
    ],
  },
  {
    perm_key: 'config.employer_sales_contacts.view',
    name: '销售联系方式',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/employer-sales-contacts',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/config/employer-sales-contacts',
      'PUT /api/admin/config/employer-sales-contacts',
    ],
  },
  {
    perm_key: 'config.locations.view',
    name: '地区管理',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/locations',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/locations/tree',
      'GET /api/admin/locations/countries',
      'GET /api/admin/locations/configs/{country_code}',
    ],
  },
  {
    perm_key: 'config.jd_complaint_types.view',
    name: '岗位投诉类型',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/jd-complaint-types',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/dictionaries'],
  },
  {
    perm_key: 'config.jd_feedback_types.view',
    name: '岗位反馈类型',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/jd-feedback-types',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/dictionaries'],
  },
  {
    perm_key: 'config.benefit_guarantee_tags.view',
    name: '标签词库',
    perm_type: 'page',
    module: 'config',
    parent_key: 'config',
    route_path: '/config/benefit-guarantee-tags',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/dictionaries/tag-lexicons',
      'POST /api/admin/dictionaries/tag-lexicons',
      'PUT /api/admin/dictionaries/tag-lexicons/{lexicon_id}',
      'DELETE /api/admin/dictionaries/tag-lexicons/{lexicon_id}',
    ],
  },
  // ---------- 反馈与投诉 ----------
  {
    perm_key: 'feedback',
    name: '反馈与投诉',
    perm_type: 'menu',
    module: 'feedback',
    route_path: '/feedback',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'message', order: 400 },
    api_routes: ['GET /api/admin/feedbacks'],
  },
  {
    perm_key: 'feedback.list.view',
    name: '意见反馈列表',
    perm_type: 'page',
    module: 'feedback',
    parent_key: 'feedback',
    route_path: '/feedback/list',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/feedbacks',
      'GET /api/admin/feedbacks/{feedback_id}',
      'GET /api/admin/feedbacks/job-taxonomy-search',
      'PUT /api/admin/feedbacks/job-taxonomy-search/{feedback_id}',
    ],
  },
  {
    perm_key: 'feedback.im_presets.view', name: 'IM常用语', perm_type: 'page', module: 'feedback', parent_key: 'feedback',
    route_path: '/feedback/im-presets', status: 'active', source: 'frontend', api_routes: ['GET /api/admin/im-presets'],
  },
  {
    perm_key: 'feedback.im_presets.configure', name: '配置常用语', perm_type: 'button', module: 'feedback', parent_key: 'feedback.im_presets.view',
    status: 'active', source: 'frontend', api_routes: ['PUT /api/admin/im-presets'],
  },
  {
    perm_key: 'feedback.match_feedback.view',
    name: 'IM匹配反馈', perm_type: 'page', module: 'feedback', parent_key: 'feedback',
    route_path: '/feedback/match-feedback', status: 'active', source: 'frontend',
    api_routes: ['GET /api/admin/match-feedback/reasons', 'GET /api/admin/match-feedback/records'],
  },
  {
    perm_key: 'feedback.match_feedback.configure', name: '配置匹配反馈原因', perm_type: 'button',
    module: 'feedback', parent_key: 'feedback.match_feedback.view', status: 'active', source: 'frontend',
    api_routes: ['PUT /api/admin/match-feedback/reasons'],
  },
  {
    perm_key: 'feedback.match_feedback.process', name: '处理匹配反馈记录', perm_type: 'button',
    module: 'feedback', parent_key: 'feedback.match_feedback.view', status: 'active', source: 'frontend',
    api_routes: ['PUT /api/admin/match-feedback/records/{mark_id}'],
  },
  {
    perm_key: 'feedback.job_taxonomy_search.view',
    name: '工种搜索反馈',
    perm_type: 'page',
    module: 'feedback',
    parent_key: 'feedback',
    route_path: '/feedback/job-taxonomy-search',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/feedbacks/job-taxonomy-search',
      'PUT /api/admin/feedbacks/job-taxonomy-search/{feedback_id}',
    ],
  },
  {
    perm_key: 'feedback.job_complaints.view',
    name: '岗位投诉列表',
    perm_type: 'page',
    module: 'feedback',
    parent_key: 'feedback',
    route_path: '/feedback/job-complaints',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/feedbacks', 'GET /api/admin/feedbacks/{feedback_id}'],
  },
  {
    perm_key: 'feedback.job_complaints.export',
    name: '导出岗位投诉',
    perm_type: 'button',
    module: 'feedback',
    parent_key: 'feedback.job_complaints.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/feedbacks'],
  },
  {
    perm_key: 'feedback.resume_complaints.view',
    name: '简历投诉列表',
    perm_type: 'page',
    module: 'feedback',
    parent_key: 'feedback',
    route_path: '/feedback/resume-complaints',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/feedbacks/resume-complaints',
      'GET /api/admin/feedbacks/{feedback_id}',
    ],
  },
  {
    perm_key: 'feedback.resume_complaints.export',
    name: '导出简历投诉',
    perm_type: 'button',
    module: 'feedback',
    parent_key: 'feedback.resume_complaints.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/feedbacks/resume-complaints'],
  },
  {
    perm_key: 'feedback.resume_complaints.restrict_user',
    name: '处理简历投诉用户限制',
    perm_type: 'button',
    module: 'feedback',
    parent_key: 'feedback.resume_complaints.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/feedbacks/resume-complaints/{feedback_id}/restriction'],
  },
  {
    perm_key: 'feedback.account_appeals.view',
    name: '账号申诉列表',
    perm_type: 'page',
    module: 'feedback',
    parent_key: 'feedback',
    route_path: '/feedback/account-appeals',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/account-appeals',
      'GET /api/admin/account-appeals/{appeal_id}',
    ],
  },
  {
    perm_key: 'feedback.account_appeals.approve',
    name: '通过申诉并解除限制',
    perm_type: 'button',
    module: 'feedback',
    parent_key: 'feedback.account_appeals.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PUT /api/admin/account-appeals/{appeal_id}/approve'],
  },
  {
    perm_key: 'feedback.jd_feedback_records.view',
    name: '岗位反馈记录',
    perm_type: 'page',
    module: 'feedback',
    parent_key: 'feedback',
    route_path: '/feedback/jd-feedback-records',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/jds/feedback-records',
      'GET /api/admin/jds/feedback-records/{record_id}',
    ],
  },
  {
    perm_key: 'feedback.jd_feedback_records.export',
    name: '导出岗位反馈记录',
    perm_type: 'button',
    module: 'feedback',
    parent_key: 'feedback.jd_feedback_records.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/jds/feedback-records'],
  },
  // ---------- 内容管理 ----------
  {
    perm_key: 'content',
    name: '内容管理',
    perm_type: 'menu',
    module: 'content',
    route_path: '/content',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'read', order: 450 },
    api_routes: ['GET /api/admin/content/articles'],
  },
  {
    perm_key: 'content.articles.view',
    name: '资讯发布',
    perm_type: 'page',
    module: 'content',
    parent_key: 'content',
    route_path: '/content/articles',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/content/articles',
      'GET /api/admin/content/media-partners',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'content.articles.create',
    name: '新建资讯',
    perm_type: 'button',
    module: 'content',
    parent_key: 'content.articles.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'POST /api/admin/content/articles',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'content.articles.update',
    name: '编辑资讯',
    perm_type: 'button',
    module: 'content',
    parent_key: 'content.articles.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'PATCH /api/admin/content/articles/{article_id}',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'content.articles.delete',
    name: '删除资讯',
    perm_type: 'button',
    module: 'content',
    parent_key: 'content.articles.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/content/articles/{article_id}'],
  },
  {
    perm_key: 'content.media_partners.view',
    name: '合作媒体',
    perm_type: 'page',
    module: 'content',
    parent_key: 'content',
    route_path: '/content/media-partners',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/content/media-partners',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'content.media_partners.create',
    name: '新建合作媒体',
    perm_type: 'button',
    module: 'content',
    parent_key: 'content.media_partners.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'POST /api/admin/content/media-partners',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'content.media_partners.update',
    name: '编辑合作媒体',
    perm_type: 'button',
    module: 'content',
    parent_key: 'content.media_partners.view',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'PATCH /api/admin/content/media-partners/{partner_id}',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'content.media_partners.delete',
    name: '删除合作媒体',
    perm_type: 'button',
    module: 'content',
    parent_key: 'content.media_partners.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/content/media-partners/{partner_id}'],
  },
  // ---------- Telegram 矩阵 ----------
  {
    perm_key: 'telegram',
    name: 'Telegram矩阵',
    perm_type: 'menu',
    module: 'telegram',
    route_path: '/telegram',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'send', order: 500 },
    api_routes: ['GET /api/admin/telegram-matrix/accounts'],
  },
  {
    perm_key: 'telegram.accounts.view',
    name: '账号管理',
    perm_type: 'page',
    module: 'telegram',
    parent_key: 'telegram',
    route_path: '/telegram/accounts',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/telegram-matrix/accounts',
      'GET /api/admin/telegram-matrix/accounts/{account_id}/dialogs',
      'GET /api/admin/telegram-matrix/accounts/auth-tasks',
      'POST /api/admin/telegram-matrix/accounts/auth',
      'GET /api/admin/telegram-matrix/clients',
      'POST /api/admin/telegram-matrix/clients',
      'GET /api/admin/telegram-matrix/clients/{client_id}',
      'PATCH /api/admin/telegram-matrix/clients/{client_id}',
    ],
  },
  {
    perm_key: 'telegram.entities.view',
    name: '会话管理',
    perm_type: 'page',
    module: 'telegram',
    parent_key: 'telegram',
    route_path: '/telegram/entities',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/telegram-matrix/accounts/discovered-entities'],
  },
  {
    perm_key: 'telegram.dashboard.view',
    name: 'Dashboard',
    perm_type: 'page',
    module: 'telegram',
    parent_key: 'telegram',
    route_path: '/telegram/dashboard',
    status: 'active',
    source: 'frontend',
    api_routes: ['GET /api/admin/telegram-matrix/dashboard'],
  },
  {
    perm_key: 'telegram.runner.manage',
    name: 'Telegram Matrix Runner 管理',
    perm_type: 'button',
    module: 'telegram',
    parent_key: 'telegram',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/telegram-matrix/accounts/runner/health',
      'POST /api/admin/telegram-matrix/accounts/runner/pause',
      'POST /api/admin/telegram-matrix/accounts/runner/resume',
      'POST /api/admin/telegram-matrix/accounts/runner/reload-policy',
      'POST /api/admin/telegram-matrix/accounts/runner/run-once',
    ],
  },
  // ---------- Facebook 矩阵 ----------
  {
    perm_key: 'facebook_matrix',
    name: 'Facebook矩阵',
    perm_type: 'menu',
    module: 'facebook_matrix',
    route_path: '/facebook-matrix',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'facebook', order: 505 },
    api_routes: ['GET /api/admin/facebook-matrix/entities'],
  },
  {
    perm_key: 'facebook_matrix.entities.view',
    name: '数据源配置',
    perm_type: 'page',
    module: 'facebook_matrix',
    parent_key: 'facebook_matrix',
    route_path: '/facebook-matrix/entities',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/facebook-matrix/entities',
      'GET /api/admin/facebook-matrix/entities/{entity_id}',
    ],
  },
  {
    perm_key: 'facebook_matrix.entity.create',
    name: '新建数据源',
    perm_type: 'button',
    module: 'facebook_matrix',
    parent_key: 'facebook_matrix.entities.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/facebook-matrix/entities'],
  },
  {
    perm_key: 'facebook_matrix.entity.update',
    name: '编辑数据源',
    perm_type: 'button',
    module: 'facebook_matrix',
    parent_key: 'facebook_matrix.entities.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['PATCH /api/admin/facebook-matrix/entities/{entity_id}'],
  },
  {
    perm_key: 'facebook_matrix.entity.delete',
    name: '删除数据源',
    perm_type: 'button',
    module: 'facebook_matrix',
    parent_key: 'facebook_matrix.entities.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/facebook-matrix/entities/{entity_id}'],
  },
  // ---------- 推广渠道 ----------
  {
    perm_key: 'ad_channels',
    name: '推广渠道管理',
    perm_type: 'menu',
    module: 'ad_channels',
    route_path: '/ad-channels',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'shareAlt', order: 600 },
    api_routes: [
      'GET /api/admin/ad-channels/available',
      'GET /api/admin/ad-channels/generate-link',
    ],
  },
  // ---------- 商业化管理 ----------
  {
    perm_key: 'commercial',
    name: '商业化管理',
    perm_type: 'menu',
    module: 'commercial',
    route_path: '/commercial',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'payCircle', order: 650 },
  },
  {
    perm_key: 'commercial.products.view',
    name: '商品管理',
    perm_type: 'page',
    module: 'commercial',
    parent_key: 'commercial',
    route_path: '/commercial/products',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/commercial/skus',
      'GET /api/admin/commercial/skus/{sku_id}',
      'POST /api/admin/commercial/skus',
      'PUT /api/admin/commercial/skus/{sku_id}',
      'PUT /api/admin/commercial/skus/{sku_id}/benefits',
      'PUT /api/admin/commercial/skus/{sku_id}/platform-products/{platform}',
      'POST /api/admin/commercial/skus/{sku_id}/platform-products/{platform}/validate',
      'POST /api/admin/commercial/skus/{sku_id}/publish',
      'POST /api/admin/commercial/skus/{sku_id}/disable',
      'POST /api/admin/commercial/readiness/check',
      'GET /api/admin/commercial/benefit-definitions',
    ],
  },
  {
    perm_key: 'commercial.benefits.view',
    name: '权益配置',
    perm_type: 'page',
    module: 'commercial',
    parent_key: 'commercial',
    route_path: '/commercial/benefits',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/commercial/benefit-definitions',
      'PUT /api/admin/commercial/benefit-definitions/{benefit_code}',
    ],
  },
  {
    perm_key: 'commercial.paywalls.view',
    name: '页面配置',
    perm_type: 'page',
    module: 'commercial',
    parent_key: 'commercial',
    route_path: '/commercial/paywalls',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/commercial/paywalls/{scene}',
      'PUT /api/admin/commercial/paywalls/{scene}',
      'POST /api/admin/commercial/paywalls/{scene}/publish',
      'POST /api/admin/commercial/paywalls/{scene}/disable',
      'GET /api/admin/commercial/skus',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'commercial.orders.view',
    name: '订单管理',
    perm_type: 'page',
    module: 'commercial',
    parent_key: 'commercial',
    route_path: '/commercial/orders',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/commercial/orders',
      'GET /api/admin/commercial/orders/{order_id}',
      'POST /api/admin/commercial/orders/{order_id}/reconcile',
      'POST /api/admin/commercial/orders/{order_id}/mark-reviewed',
    ],
  },
  // ---------- 运营工具 ----------
  {
    perm_key: 'tools',
    name: '运营工具',
    perm_type: 'menu',
    module: 'tools',
    route_path: '/tools',
    status: 'active',
    source: 'frontend',
    meta_json: { icon: 'tool', order: 700 },
    api_routes: ['GET /api/admin/mobile-push-tests/options'],
  },
  {
    perm_key: 'tools.mobile_push.view',
    name: '移动推送测试页面',
    perm_type: 'page',
    module: 'tools',
    parent_key: 'tools',
    route_path: '/tools/mobile-push',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/mobile-push-tests/options',
      'POST /api/admin/mobile-push-tests/preview',
      'GET /api/admin/jds',
      'GET /api/admin/dictionaries/tree',
    ],
  },
  {
    perm_key: 'tools.telegram_push.view',
    name: 'Bot 促活测试',
    perm_type: 'page',
    module: 'tools',
    parent_key: 'tools',
    route_path: '/tools/telegram-push',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/telegram-push-tests/options',
      'POST /api/admin/telegram-push-tests/preview',
    ],
  },
  {
    perm_key: 'tools.telegram_push.send',
    name: '发送 Bot 促活测试',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.telegram_push.view',
    route_path: '/tools/telegram-push',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/telegram-push-tests/send'],
  },
  {
    perm_key: 'tools.home_marquee_banners.view',
    name: '首页跑马灯 Banner',
    perm_type: 'page',
    module: 'tools',
    parent_key: 'tools',
    route_path: '/tools/home-marquee-banners',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/home-marquee-banners',
      'GET /api/admin/home-marquee-banners/options',
    ],
  },
  {
    perm_key: 'tools.home_marquee_banners.create',
    name: '新增首页跑马灯 Banner',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.home_marquee_banners.view',
    route_path: '/tools/home-marquee-banners',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'POST /api/admin/home-marquee-banners',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'tools.home_marquee_banners.update',
    name: '编辑及排序首页跑马灯 Banner',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.home_marquee_banners.view',
    route_path: '/tools/home-marquee-banners',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'PUT /api/admin/home-marquee-banners/{banner_id}',
      'PUT /api/admin/home-marquee-banners/{banner_id}/status',
      'PUT /api/admin/home-marquee-banners/reorder',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'tools.home_marquee_banners.delete',
    name: '删除首页跑马灯 Banner',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.home_marquee_banners.view',
    route_path: '/tools/home-marquee-banners',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/home-marquee-banners/{banner_id}'],
  },
  {
    perm_key: 'tools.home_shortcuts.view',
    name: '首页金刚位',
    perm_type: 'page',
    module: 'tools',
    parent_key: 'tools',
    route_path: '/tools/home-shortcuts',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'GET /api/admin/home-shortcuts',
      'GET /api/admin/home-shortcuts/options',
    ],
  },
  {
    perm_key: 'tools.home_shortcuts.create',
    name: '新增首页金刚位',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.home_shortcuts.view',
    route_path: '/tools/home-shortcuts',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'POST /api/admin/home-shortcuts',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'tools.home_shortcuts.update',
    name: '编辑及排序首页金刚位',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.home_shortcuts.view',
    route_path: '/tools/home-shortcuts',
    status: 'active',
    source: 'frontend',
    api_routes: [
      'PUT /api/admin/home-shortcuts/{shortcut_id}',
      'PUT /api/admin/home-shortcuts/{shortcut_id}/status',
      'PUT /api/admin/home-shortcuts/reorder',
      'POST /api/admin/files/upload-url',
      'POST /api/admin/files/upload',
    ],
  },
  {
    perm_key: 'tools.home_shortcuts.delete',
    name: '删除首页金刚位',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.home_shortcuts.view',
    route_path: '/tools/home-shortcuts',
    status: 'active',
    source: 'frontend',
    api_routes: ['DELETE /api/admin/home-shortcuts/{shortcut_id}'],
  },
  {
    perm_key: 'tools.mobile_push.send',
    name: '发送移动推送测试',
    perm_type: 'button',
    module: 'tools',
    parent_key: 'tools.mobile_push.view',
    status: 'active',
    source: 'frontend',
    api_routes: ['POST /api/admin/mobile-push-tests/send'],
  },
];

export const permissionManifestMap = permissionManifest.reduce<
  Record<string, PermissionManifestItem>
>((acc, item) => {
  acc[item.perm_key] = item;
  return acc;
}, {});
